import { useState, useEffect, useMemo } from 'react'
import { Box, Switch, FormControlLabel, Typography } from '@mui/material'
import { useCurrentUser } from '~/common/use-current-user'
import { useOrgParams } from '~/common/use-org-params'
import { supabaseGetter } from '~/api/supabase-getter'
import { GddChart } from './gdd-chart'

// GDD base labels vary by temperature unit
const gddLabel = (base, isCelsius) =>
  `GDD ${base === '5' ? (isCelsius ? '5' : '41') : (isCelsius ? '10' : '50')}`

const FROST_WINDOW_DAYS = 70 // 10 weeks

// Convert a DOY to a Date in the current calendar year (UTC)
const doyToThisYear = (doy) => {
  const year = new Date().getFullYear()
  return new Date(Date.UTC(year, 0, doy))
}

// Returns the nearest upcoming frost estimate within the window,
// or null if none or if a matching report already exists.
const getUpcomingFrost = (avgLfd, avgFfd, reports) => {
  const today = Date.now()
  const reportCodes = new Set(reports.map(r => r.report_code))

  const candidates = [
    avgLfd > 0 ? { doy: Math.round(avgLfd), label: 'LFD', type: 'lfd' } : null,
    avgFfd > 0 ? { doy: Math.round(avgFfd), label: 'FFD', type: 'ffd' } : null,
  ]
    .filter(Boolean)
    .filter(f => !reportCodes.has(f.type))
    .map(f => ({ ...f, diffDays: Math.round((doyToThisYear(f.doy) - today) / 86400000) }))
    .filter(f => f.diffDays > 0 && f.diffDays <= FROST_WINDOW_DAYS)
    .sort((a, b) => a.diffDays - b.diffDays)

  return candidates[0] ?? null
}

const FrostNotice = ({ frost }) => {
  const weeks = Math.floor(frost.diffDays / 7)
  const days = frost.diffDays % 7
  const frostType = frost.label === 'LFD' ? 'last' : 'first'

  return (
    <Typography
      variant="caption"
      sx={{ fontSize: '0.65rem', lineHeight: 1.25, mb: 0.25, flexShrink: 0, display: 'block', color: 'text.secondary' }}
    >
      <Typography
        variant='subtitle2'
        component="span"
        sx={{ fontWeight: 700, color: ({ palette }) => palette.mode === 'dark' ? 'primary.light' : 'primary.main' }}
      >
        {weeks > 0 ? `${weeks}w ` : ''}{days}d
      </Typography>
      {' until estimated '}{frostType} frost date
    </Typography>
  )
}

export const ClimatePanel = () => {
  const [gddBase, setGddBase] = useState('5')
  const [climate, setClimate] = useState(null)
  const [reports, setReports] = useState([])
  const { userPreferences } = useCurrentUser()
  const { farmId } = useOrgParams()
  const tempUnit = userPreferences?.temperature_unit || '°C'
  const isCelsius = tempUnit === '°C'

  useEffect(() => {
    if (!farmId) return
    supabaseGetter.climates.getByOrganization(farmId).then(({ data }) => {
      setClimate(data ?? null)
    })
    supabaseGetter.reports.getByOrganization(farmId).then(({ data }) => {
      setReports(data ?? [])
    })
  }, [farmId])

  const upcomingFrost = useMemo(() => {
    if (!climate) return null
    return getUpcomingFrost(climate.avg_lfd, climate.avg_ffd, reports)
  }, [climate, reports])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: 1, pt: 0.5, pb: 0 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, mb: 0.25 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
          SEASON PROGRESS
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={gddBase === '10'}
              onChange={(_, checked) => setGddBase(checked ? '10' : '5')}
              sx={{ transform: 'scale(0.7)', ml: 0 }}
            />
          }
          label={
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
              {gddLabel(gddBase, isCelsius)}
            </Typography>
          }
          labelPlacement="start"
          sx={{ m: 0, gap: 0 }}
        />
      </Box>

      {/* Frost countdown — shown only when within window and no report filed */}
      {upcomingFrost && <FrostNotice frost={upcomingFrost} />}

      {/* Chart fills remaining space */}
      <Box sx={{ flex: 1, minHeight: 0, mt: upcomingFrost ? 0.5 : 0 }}>
        <GddChart
          gddBase={gddBase}
          tempUnit={tempUnit}
          gdd5Series={climate?.gdd5_series ?? null}
          gdd10Series={climate?.gdd10_series ?? null}
          lfd={climate?.avg_lfd ? Math.round(climate.avg_lfd) : null}
          ffd={climate?.avg_ffd ? Math.round(climate.avg_ffd) : null}
        />
      </Box>
    </Box>
  )
}
